import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Globe, Lock, Radio, MessageSquare, BrainCircuit, BarChart3, ArrowRight, ChevronLeft, Shield, Camera, Image as ImageIcon, Key, Sparkles, Info, Users, Zap, Loader, Search, X } from 'lucide-react';
import { cn, getRoomPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { useAuth } from '../context/AuthContext';
import { interests } from '../utils/interests';
import { useCreation } from '../hooks/useCreation';
import { themeClasses } from '../utils/themes';
import Breadcrumbs from '../components/Breadcrumbs';
import { handleImageUpload } from '../utils/imageUtils';

const TypeCard = ({ icon: Icon, label, description, selected, onClick, color, activeTextColor }) => (
  <button 
    onClick={onClick}
    className={cn(
      "w-full flex items-start gap-4 p-4 rounded-3xl border-2 transition-all active:scale-[0.98] text-left group",
      selected ? (activeTextColor ? "bg-white border-2" : "border-brand-indigo bg-brand-indigo/5") : "border-gray-50 bg-white hover:border-gray-100"
    )}
    style={selected && activeTextColor ? { borderColor: 'currentColor', color: activeTextColor } : {}}
  >
    <div className={cn(
      "p-2.5 rounded-xl transition-transform group-hover:scale-110",
      selected ? (color || "bg-brand-indigo") + " text-white shadow-md shadow-brand-indigo/20" : "bg-gray-50 text-gray-400"
    )}>
      <Icon size={18} />
    </div>
    <div className="flex-1">
      <h4 className={cn("font-bold text-sm", selected ? (activeTextColor || "text-brand-indigo") : "text-gray-900")}>{label}</h4>
      <p className="text-xs2 text-gray-500 font-medium leading-tight mt-0.5">{description}</p>
    </div>
  </button>
);

const roomTypeMetadata = {
  mixed: {
    title: 'Launch a Mixed Session',
    tip1: { label: 'Mixed Sessions', text: 'are best for long events where you need Q&A, brainstorming, and polls in one place.' },
    tip2: { label: 'Clear Titles', text: 'like "Project Phoenix Brainstorm" help participants find your room quickly.' },
    moderation: "As the host, you'll have full control over pinning questions, clustering ideas, and muting disruptive participants.",
    descriptionPlaceholder: "What will we interact about?",
    buttonText: "Launch Session"
  },
  qa: {
    title: 'Start a Pure Q&A',
    tip1: { label: 'Pure Q&A', text: 'is perfect for AMA style events or structured expert interviews.' },
    tip2: { label: 'Upvoting', text: 'allows you to focus on the most popular questions from the community.' },
    moderation: "You can pin important questions, moderate incoming ones, and mark questions as answered in real-time.",
    descriptionPlaceholder: "What's the focus of this Q&A session?",
    buttonText: "Start Q&A"
  },
  brainstorm: {
    title: 'Open an Ideation Hub',
    tip1: { label: 'Ideation Hubs', text: 'work best for collaborative problem solving and feature request collections.' },
    tip2: { label: 'Clear Goals', text: 'help keep brainstorming sessions focused and actionable.' },
    moderation: "Manage idea cards, cluster similar thoughts using AI, and enable community voting on the best proposals.",
    descriptionPlaceholder: "What are we brainstorming today?",
    buttonText: "Open Hub"
  },
  poll: {
    title: 'Create a Live Decision',
    tip1: { label: 'Live Decision', text: 'rooms are ideal for quick community consensus or interactive workshops.' },
    tip2: { label: 'Poll Variety', text: 'helps keep the audience engaged throughout the decision-making process.' },
    moderation: "Launch polls instantly, show real-time results, and guide the community through different voting phases.",
    descriptionPlaceholder: "What decisions are we making today?",
    buttonText: "Create Decision Room"
  }
};

const RoomCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const community_id_raw = searchParams.get('community_id');
  const community_id = (community_id_raw === 'null' || community_id_raw === 'undefined' || !community_id_raw) ? null : community_id_raw;
  const typeParam = searchParams.get('type');
  const { addRoom } = useActivities();
  const speakerSearchAllowed = Boolean(community_id || user?.role === 'platform_admin');
  
  const {
    step,
    setStep,
    loading,
    setLoading,
    error,
    setError,
    image: sessionImage,
    setImage: setSessionImage,
    fileInputRef,
    speakers,
    speakerInput,
    setSpeakerInput,
    addSpeaker,
    removeSpeaker,
    searchMembers,
    searchResults,
    searching,
    nextStep: triggerNextStep,
    prevStep,
  } = useCreation();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setError('');
        const resizedImage = await handleImageUpload(file, { maxSizeMB: 2 });
        setSessionImage(resizedImage);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const TITLE_LIMIT = 100;
  const DESCRIPTION_LIMIT = 1000;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (speakerInput.trim().length >= 2 && speakerSearchAllowed) {
        searchMembers(speakerInput, community_id, user?.role === 'platform_admin');
      } else {
        searchMembers('', community_id, speakerSearchAllowed);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [speakerInput, community_id, searchMembers, speakerSearchAllowed, user?.role]);

  const [roomType, setRoomType] = useState(typeParam && roomTypeMetadata[typeParam] ? typeParam : 'mixed');
  const currentMetadata = roomTypeMetadata[roomType] || roomTypeMetadata.mixed;
  const [privacy, setPrivacy] = useState('public');
  const [password, setPassword] = useState('');
  const [inviteOnly, setInviteOnly] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [timeframe, setTimeframe] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('1h');

  const theme = themeClasses[roomType] || themeClasses['mixed'];

  const handleCreate = async () => {
    const validate = () => {
      if (!title.trim()) return 'Please enter a session title';
      if (title.trim().length < 5) return 'Title must be at least 5 characters long';
      if (title.length > TITLE_LIMIT) return `Title is too long (max ${TITLE_LIMIT} chars)`;
      if (description.trim().length < 20) return 'Description must be at least 20 characters long';
      if (description.length > DESCRIPTION_LIMIT) return `Description is too long (max ${DESCRIPTION_LIMIT} chars)`;
      if (timeframe === 'scheduled' && !scheduledAt) return 'Please select a scheduled date and time';
      if (!category) return 'Please select a category';
      if (privacy === 'private' && !password.trim()) return 'Password is required for private sessions';
      return null;
    };

    const errorMsg = validate();
    if (errorMsg) {
      setError(errorMsg);
      if (!title.trim()) setStep(1);
      return;
    }

    setLoading(true);
    setError('');

    const typeMap = {
      'mixed': 'Mixed',
      'qa': 'Q&A',
      'brainstorm': 'Brainstorm',
      'poll': 'Poll'
    };
    
    try {
      const newRoom = await addRoom({
        title,
        description,
        type: typeMap[roomType] || 'Live',
        privacy,
        template: roomType === 'brainstorm' ? 'blank' : null,
        anonymous: roomType === 'brainstorm' ? true : false,
        voting: roomType === 'brainstorm' ? true : false,
        settings: {
          password: privacy === 'private' ? password : null,
          invite_only: privacy === 'private' ? inviteOnly : false,
          speakers: speakers.length > 0 ? speakers.map(s => s.name) : null,
          category: category,
          duration: duration // Put duration explicitly in settings
        },
        image: sessionImage,
        community_id: community_id,
        timeframe,
        scheduled_at: timeframe === 'scheduled' ? scheduledAt : null,
        participants: 0,
        host: user?.name || 'You',
        options: roomType === 'poll' ? [] : null // Ensure options is sent for polls
      });
      navigate(getRoomPath(newRoom));
    } catch (err) {
      console.error('Room creation failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to create session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    triggerNextStep(() => {
      if (!title.trim()) return 'Please enter a session title';
      if (title.trim().length < 5) return 'Title must be at least 5 characters long';
      if (title.length > TITLE_LIMIT) return `Title is too long (max ${TITLE_LIMIT} chars)`;
      if (!category) return 'Please select a category';
      return null;
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => step > 1 ? prevStep() : navigate(-1)}
          className="p-2.5 hover:bg-gray-100 rounded-xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <div className="mb-1">
            <Breadcrumbs 
              items={[
                { label: 'Explore', path: '/explore' },
                community_id ? { label: 'Community', path: `/c/${community_id}` } : null,
                { label: 'New Session' }
              ].filter(Boolean)} 
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{currentMetadata.title}</h1>
          <p className="text-gray-500 font-medium text-sm2">Step {step} of 2: {step === 1 ? 'Configure Interaction' : 'Schedule & Visibility'}</p>
        </div>
      </div>

      {step === 1 ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
              <Shield size={16} />
              {error}
            </div>
          )}
          <div className={cn("p-6 rounded-3xl border-2 flex flex-col md:flex-row items-center gap-6", theme.lightBg, theme.lightBorder)}>
            <div className={cn("w-20 h-20 rounded-2xl flex items-center justify-center shrink-0 shadow-lg", theme.bg, "text-white")}>
              <Shield size={40} />
            </div>
            <div className="text-center md:text-left">
              <h3 className={cn("text-xl font-bold mb-1", theme.text)}>Mixed Session Hub</h3>
              <p className="text-sm font-medium text-gray-500 leading-relaxed max-w-md">
                This is our most powerful room type. You get a full suite of interactive tools: 
                <span className="font-bold text-gray-700"> Live Q&A</span>, 
                <span className="font-bold text-gray-700"> Ideation Boards</span>, and 
                <span className="font-bold text-gray-700"> Instant Polling</span>.
              </p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-2 flex-1">
              <div className="px-3 py-1.5 bg-white rounded-lg border border-gray-100 text-[10px] font-bold uppercase tracking-[0.15em] text-purple-600 flex items-center gap-1.5 shadow-sm">
                <MessageSquare size={12} /> Q&A
              </div>
              <div className="px-3 py-1.5 bg-white rounded-lg border border-gray-100 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-600 flex items-center gap-1.5 shadow-sm">
                <BrainCircuit size={12} /> Ideas
              </div>
              <div className="px-3 py-1.5 bg-white rounded-lg border border-gray-100 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-indigo flex items-center gap-1.5 shadow-sm">
                <BarChart3 size={12} /> Polls
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 p-6 space-y-5">
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            <div>
              <label className="block text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Session Cover Image</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="relative h-48 rounded-2xl bg-gray-50 border-2 border-dashed border-gray-100 flex flex-col items-center justify-center cursor-pointer group hover:bg-gray-100 transition-all overflow-hidden"
              >
                {sessionImage ? (
                  <>
                    <img src={sessionImage} alt="Session cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera size={32} className="text-white" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 rounded-full bg-white shadow-sm mb-2 text-gray-300 group-hover:text-brand-indigo transition-colors">
                      <ImageIcon size={32} />
                    </div>
                    <p className="text-xs font-bold text-gray-400">Click to upload image</p>
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">Category / Primary Interest</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {interests.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => setCategory(i.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all active:scale-95 text-left",
                      category === i.id 
                        ? (theme.border + " " + theme.lightBg + " " + theme.text)
                        : "border-gray-50 bg-white text-gray-400 hover:border-gray-100"
                    )}
                  >
                    <span className="shrink-0">{i.icon}</span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] truncate">{i.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Session Title</label>
                <span className={cn("text-[10px] font-bold", title.length > TITLE_LIMIT ? "text-red-500" : "text-gray-400")}>
                  {title.length}/{TITLE_LIMIT}
                </span>
              </div>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_LIMIT + 10))}
                placeholder="e.g. Weekly Ecosystem Update"
                className={cn(
                  "w-full px-5 py-3.5 bg-gray-50 border-none rounded-xl font-bold transition-all text-base focus:ring-2",
                  theme.ring,
                  title.length > TITLE_LIMIT && "ring-red-500/20"
                )}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Topic / Description</label>
                <span className={cn("text-[10px] font-bold", description.length > DESCRIPTION_LIMIT ? "text-red-500" : "text-gray-400")}>
                  {description.length}/{DESCRIPTION_LIMIT}
                </span>
              </div>
              <textarea 
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT + 100))}
                placeholder={currentMetadata.descriptionPlaceholder}
                className={cn(
                  "w-full px-5 py-3.5 bg-gray-50 border-none rounded-xl font-medium transition-all resize-none text-sm focus:ring-2",
                  theme.ring,
                  description.length > DESCRIPTION_LIMIT && "ring-red-500/20"
                )}
              ></textarea>
            </div>
            <div>
              <label className="block text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">Speaker(s)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                <div className={cn("px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 border", theme.lightBg, theme.text, theme.lightBorder)}>
                  <Shield size={14} />
                  You (Host)
                </div>
                {speakers.map((speaker) => (
                  <div key={speaker.id} className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl font-bold text-sm flex items-center gap-2 border border-gray-100">
                    <div className="w-5 h-5 rounded-full bg-brand-indigo/10 flex items-center justify-center overflow-hidden shrink-0">
                      {speaker.avatar ? <img src={speaker.avatar} alt={speaker.name} className="w-full h-full object-cover" /> : <Users size={12} className="text-brand-indigo" />}
                    </div>
                    {speaker.name}
                    <button onClick={() => removeSpeaker(speaker.id)} className="ml-1 hover:text-red-500 transition-colors font-bold text-gray-400">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="relative">
                <div className="relative">
                  <input 
                    type="text" 
                    value={speakerInput}
                    onChange={(e) => setSpeakerInput(e.target.value)}
                    placeholder={community_id ? "Search community members..." : speakerSearchAllowed ? "Search platform members..." : "Only platform admins can assign speakers outside a community"}
                    disabled={!speakerSearchAllowed}
                    className={cn(
                      "w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl font-medium text-sm transition-all focus:ring-2",
                      theme.ring,
                      !speakerSearchAllowed && "cursor-not-allowed opacity-60"
                    )}
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {searching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                  </div>
                </div>

                <AnimatePresence>
                  {searchResults.length > 0 && speakerInput.trim().length >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto"
                    >
                      {searchResults.map((member) => (
                        <button
                          key={member.id}
                          onClick={() => addSpeaker(member)}
                          className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-brand-indigo/5 flex items-center justify-center overflow-hidden shrink-0">
                            {member.avatar ? <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" /> : <Users size={16} className="text-brand-indigo" />}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-900">{member.name}</p>
                            <p className="text-xxs font-medium text-gray-500">{member.email}</p>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <p className="text-xxs font-medium text-gray-400 mt-2 flex items-center gap-1.5 px-1">
                <Info size={10} />
                {community_id
                  ? 'Speakers must be current members of this community.'
                  : speakerSearchAllowed
                    ? 'Outside a community, only platform admins can assign additional speakers.'
                    : 'This session is not linked to a community, so only platform admins may add speakers.'}
              </p>
            </div>
          </div>

          <div className="bg-brand-indigo/5 rounded-3xl p-6 border border-brand-indigo/10">
            <h3 className="flex items-center gap-2 font-bold text-brand-indigo text-xs uppercase tracking-[0.15em] mb-4">
              <Sparkles size={16} />
              Creation Tips
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex gap-3">
                <div className="mt-1"><Info size={14} className="text-brand-indigo" /></div>
                <p className="text-xs font-medium text-gray-600 leading-relaxed">
                  <span className="font-bold text-gray-900">{currentMetadata.tip1.label}</span> {currentMetadata.tip1.text}
                </p>
              </div>
              <div className="flex gap-3">
                <div className="mt-1"><Info size={14} className="text-brand-indigo" /></div>
                <p className="text-xs font-medium text-gray-600 leading-relaxed">
                  <span className="font-bold text-gray-900">{currentMetadata.tip2.label}</span> {currentMetadata.tip2.text}
                </p>
              </div>
            </div>
          </div>

          <button 
            onClick={nextStep}
            className={cn(
              "w-full text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all text-base shadow-lg",
              theme.bg,
              theme.hover,
              theme.shadow
            )}
          >
            Next Step
            <ArrowRight size={18} />
          </button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-5">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                  <Shield size={16} />
                  {error}
                </div>
              )}
              <section>
                <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">When?</h2>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => setTimeframe('now')}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-2xl border-2 transition-all",
                      timeframe === 'now' ? cn(theme.bg, "text-white shadow-md", theme.shadow, "border-transparent") : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Radio size={20} className={cn("mb-2", timeframe === 'now' && "animate-pulse")} />
                    <span className="font-bold text-sm">Go Live Now</span>
                  </button>
                  <button 
                    onClick={() => setTimeframe('scheduled')}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-2xl border-2 transition-all",
                      timeframe === 'scheduled' ? cn(theme.bg, "text-white shadow-md", theme.shadow, "border-transparent") : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Calendar size={20} className="mb-2" />
                    <span className="font-bold text-sm">Schedule</span>
                  </button>
                </div>

                {timeframe === 'scheduled' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 space-y-2.5"
                  >
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar size={14} />
                      <label className="text-xxs font-bold uppercase tracking-[0.15em]">Date & Time</label>
                    </div>
                    <input 
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className={cn(
                        "w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl font-bold text-sm transition-all focus:ring-2",
                        theme.ring
                      )}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </motion.div>
                )}
              </section>

              <section>
                <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Duration</h2>
                <div className="grid grid-cols-3 gap-2">
                  {['30m', '1h', '2h'].map((d) => (
                    <button 
                      key={d} 
                      onClick={() => setDuration(d)}
                      className={cn(
                        "py-2.5 rounded-lg font-bold transition-all text-sm",
                        duration === d ? cn(theme.bg, "text-white shadow-md", theme.shadow) : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </section>
            </div>

            <div className="space-y-5">
              <section>
                <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Visibility</h2>
                <div className="space-y-2.5">
                  <button 
                    onClick={() => setPrivacy('public')}
                    className={cn(
                      "w-full flex items-center gap-4 p-3.5 rounded-xl border-2 transition-all text-left",
                      privacy === 'public' ? cn(theme.lightBg, "border-2") : "border-gray-50 bg-white"
                    )}
                    style={privacy === 'public' ? { borderColor: 'currentColor', color: 'inherit' } : {}}
                  >
                    <Globe size={18} className={privacy === 'public' ? theme.text : "text-gray-400"} />
                    <div>
                      <p className={cn("font-bold text-sm", privacy === 'public' ? "text-gray-900" : "text-gray-900")}>Public</p>
                      <p className="text-xxs text-gray-500 font-medium">Visible to everyone in the ecosystem</p>
                    </div>
                  </button>
                  <button 
                    onClick={() => setPrivacy('private')}
                    className={cn(
                      "w-full flex items-center gap-4 p-3.5 rounded-xl border-2 transition-all text-left",
                      privacy === 'private' ? cn(theme.lightBg, "border-2") : "border-gray-50 bg-white"
                    )}
                    style={privacy === 'private' ? { borderColor: 'currentColor', color: 'inherit' } : {}}
                  >
                    <Lock size={18} className={privacy === 'private' ? theme.text : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Private / Invite Only</p>
                      <p className="text-xxs text-gray-500 font-medium">Only accessible via direct link or invite</p>
                    </div>
                  </button>
                </div>
              </section>

              {privacy === 'private' && (
                <motion.section
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 pt-2"
                >
                  <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Private Access Configuration</h2>
                  <div className={cn("bg-white rounded-2xl border p-5 space-y-4 shadow-sm", theme.lightBorder)}>
                    <button 
                      onClick={() => setInviteOnly(!inviteOnly)}
                      className="w-full flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg transition-colors", inviteOnly ? cn(theme.lightBg, theme.text) : "bg-gray-50 text-gray-400")}>
                          <Shield size={16} />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm text-gray-900">Community Only</p>
                          <p className="text-xxs text-gray-500 font-medium">Only community members can join</p>
                        </div>
                      </div>
                      <div className={cn("w-10 h-6 rounded-full relative transition-colors", inviteOnly ? theme.bg : "bg-gray-200")}>
                        <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", inviteOnly ? "right-1" : "left-1")}></div>
                      </div>
                    </button>

                    <div className="h-px bg-gray-50 w-full"></div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Key size={14} />
                        <label className="text-xxs font-bold uppercase tracking-[0.15em]">Access Password (Optional)</label>
                      </div>
                      <input 
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Leave blank for no password"
                        className={cn(
                          "w-full px-4 py-2.5 bg-gray-50 border-none rounded-xl font-bold text-sm transition-all focus:ring-2",
                          theme.ring
                        )}
                      />
                    </div>
                  </div>
                </motion.section>
              )}
            </div>
          </div>

          <div className={cn("border rounded-2xl p-4", theme.lightBg, theme.lightBorder)}>
            <div className="flex gap-4">
              <div className={cn("p-2.5 bg-white rounded-xl shadow-sm", theme.text)}>
                <Shield size={18} />
              </div>
              <div>
                <h4 className={cn("font-bold text-sm2", theme.text)}>Moderation Active</h4>
                <p className={cn("font-medium leading-relaxed mt-0.5 opacity-90", theme.text)}>
                  {currentMetadata.moderation}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-brand-indigo/5 rounded-3xl p-6 border border-brand-indigo/10">
            <h3 className="flex items-center gap-2 font-bold text-brand-indigo text-xs uppercase tracking-[0.15em] mb-4">
              <Sparkles size={16} />
              Visibility & Schedule Tips
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex gap-3">
                <div className="mt-1"><Info size={14} className="text-brand-indigo" /></div>
                <p className="text-xs font-medium text-gray-600 leading-relaxed">
                  <span className="font-bold text-gray-900">Private Rooms</span> are great for internal team meetings. Don't forget to share the password!
                </p>
              </div>
              <div className="flex gap-3">
                <div className="mt-1"><Info size={14} className="text-brand-indigo" /></div>
                <p className="text-xs font-medium text-gray-600 leading-relaxed">
                  <span className="font-bold text-gray-900">Scheduled Sessions</span> appear in the community calendar early, helping you build anticipation.
                </p>
              </div>
            </div>
          </div>

          <button 
            onClick={handleCreate}
            disabled={loading}
            className={cn(
              "w-full text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all text-base shadow-lg",
              theme.bg,
              theme.hover,
              theme.shadow,
              loading && "opacity-70 cursor-not-allowed"
            )}
          >
            {loading ? (
              <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              currentMetadata.buttonText
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default RoomCreation;
